<template>
  <form class="bwauth-form" novalidate @submit.prevent="submit">
    <div v-if="generalError" class="bwauth-alert bwauth-alert-error">
      {{ generalError }}
    </div>

    <div v-if="successMessage" class="bwauth-alert bwauth-alert-success">
      {{ successMessage }}
    </div>

    <template v-else>
      <label class="bwauth-field">
        <span>{{ $t('Email') }}</span>
        <input
          v-model.trim="form.email"
          type="email"
          name="email"
          autocomplete="email"
          :disabled="hasToken"
          :class="{ 'has-error': fieldError('email') }"
          @blur="touch('email')"
        >
        <small v-if="fieldError('email')">{{ fieldError('email') }}</small>
      </label>

      <label v-if="hasToken" class="bwauth-field">
        <span>{{ $t('New Password') }}</span>
        <div class="bwauth-password-field">
          <input
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            autocomplete="new-password"
            :class="{ 'has-error': fieldError('password') }"
            @blur="touch('password')"
          >
          <password-toggle v-model="showPassword" />
        </div>
        <small v-if="fieldError('password')">{{ fieldError('password') }}</small>
      </label>

      <label v-if="hasToken" class="bwauth-field">
        <span>{{ $t('Confirm Password') }}</span>
        <div class="bwauth-password-field">
          <input
            v-model="form.password_confirmation"
            :type="showPassword ? 'text' : 'password'"
            name="password_confirmation"
            autocomplete="new-password"
            :class="{ 'has-error': fieldError('password_confirmation') }"
            @blur="touch('password_confirmation')"
          >
          <password-toggle v-model="showPassword" />
        </div>
        <small v-if="fieldError('password_confirmation')">{{ fieldError('password_confirmation') }}</small>
      </label>

      <div class="bwauth-actions">
        <button type="submit" class="bwauth-primary-button" :disabled="loading">
          <span v-if="loading" class="bwauth-spinner"></span>
          <template v-else>{{ submitLabel }}</template>
        </button>
      </div>
    </template>

    <div v-if="showLoginLink" class="bwauth-switch-copy">
      <a class="bwauth-link" :href="loginHref" @click="switchToLogin">{{ $t('Login') }}</a>
    </div>
  </form>
</template>

<script>
import AuthApi from './AuthApi';
import PasswordToggle from './PasswordToggle.vue';
import { validationMixin } from 'vuelidate';
import {
  required, requiredIf, email, minLength, sameAs,
} from 'vuelidate/lib/validators';

export default {
  mixins: [validationMixin],
  components: {
    PasswordToggle,
  },
  props: {
    initialEmail: {
      type: String,
      default: '',
    },
    token: {
      type: String,
      default: '',
    },
    loginHref: {
      type: String,
      default: '/login',
    },
    showLoginLink: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      form: {
        email: this.initialEmail,
        password: '',
        password_confirmation: '',
        token: this.token,
      },
      touched: {},
      errors: {},
      loading: false,
      showPassword: false,
      successMessage: '',
    };
  },
  validations: {
    form: {
      email: { required, email },
      password: {
        required: requiredIf(function requiredPassword() {
          return this.hasToken;
        }),
        minLength: minLength(6),
      },
      password_confirmation: {
        required: requiredIf(function requiredPasswordConfirmation() {
          return this.hasToken;
        }),
        minLength: minLength(6),
        sameAsPassword: sameAs('password'),
      },
    },
  },
  computed: {
    hasToken() {
      return Boolean(this.form.token);
    },
    generalError() {
      if (this.errors && this.errors.message) {
        return this.errors.message;
      }
      return null;
    },
    submitLabel() {
      return this.hasToken
        ? this.$t('auth.reset_password.submit_new_password')
        : this.$t('auth.reset_password.submit_send_email');
    },
  },
  watch: {
    initialEmail(value) {
      this.form.email = value || '';
    },
    token(value) {
      this.form.token = value || '';
    },
  },
  methods: {
    touch(field) {
      if (this.$v && this.$v.form && this.$v.form[field]) {
        this.$v.form[field].$touch();
      }
    },
    fieldError(field) {
      const control = this.$v && this.$v.form ? this.$v.form[field] : null;
      if (control && control.$dirty && control.$invalid) {
        if (!control.required) {
          return this.$t('validation.required');
        }
        if (field === 'email' && !control.email) {
          return this.$t('validation.email');
        }
        if (['password', 'password_confirmation'].includes(field) && !control.minLength) {
          return this.$t('validation.txtMinLen', { n: control.$params.minLength.min });
        }
        if (field === 'password_confirmation' && !control.sameAsPassword) {
          return this.$t('validation.txtSameAsPassword');
        }
      }
      if (this.errors && this.errors[field]) {
        return Array.isArray(this.errors[field]) ? this.errors[field][0] : this.errors[field];
      }
      return null;
    },
    isValid() {
      const fields = this.hasToken
        ? ['email', 'password', 'password_confirmation']
        : ['email'];

      fields.forEach((field) => this.$v.form[field].$touch());
      return !fields.some((field) => this.$v.form[field].$invalid);
    },
    submit() {
      this.errors = {};
      this.successMessage = '';

      if (!this.isValid()) {
        return;
      }

      this.loading = true;
      this.$emit('loading', true);

      const request = this.hasToken
        ? AuthApi.resetPassword(this.form)
        : AuthApi.sendResetPasswordEmail({ email: this.form.email });

      request
        .then(({ data }) => {
          const rawMessage = typeof data === 'string'
            ? data
            : (data && (data.message || data.status)) || '';
          const message = rawMessage === 'Your password has been reset!'
            ? this.$t('auth.reset_password.success')
            : (rawMessage || this.$t('Success'));
          this.successMessage = message;
          this.$emit('success', data);
        })
        .catch((error) => {
          const response = error.response && error.response.data ? error.response.data : {};
          this.errors = response.errors || { message: response.message || this.$t('Server error') };
        })
        .finally(() => {
          this.loading = false;
          this.$emit('loading', false);
        });
    },
    switchToLogin(event) {
      if (this.$listeners['switch-to-login'] && event) {
        event.preventDefault();
      }
      this.$emit('switch-to-login');
    },
  },
};
</script>
